import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";
import { datasheetsApi, useInvalidateDatasheet, type SizeSlot } from "@/lib/datasheets";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  datasheetId: number;
  sizes: SizeSlot[];
}

const ZERO = [0, 0, 0, 0, 0, 0, 0];

export function AdjustBalanceDialog({ open, onOpenChange, datasheetId, sizes }: Props) {
  const invalidate = useInvalidateDatasheet();
  const named = useMemo(() => sizes.filter((s) => s.desc), [sizes]);

  const [deltas, setDeltas] = useState<number[]>(ZERO);
  const [delivery, setDelivery] = useState(0);
  const [fitting, setFitting] = useState(0);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const setDelta = (index: number, value: number) =>
    setDeltas((prev) => prev.map((q, i) => (i === index - 1 ? value : q)));
  const hasDelta = deltas.some((q) => q !== 0) || delivery !== 0 || fitting !== 0;
  const canSave = hasDelta && reason.trim().length > 0 && !submitting;

  async function submit() {
    setSubmitting(true);
    try {
      await datasheetsApi.addPurchase(datasheetId, {
        purchaseDate: new Date().toISOString(),
        sizeQuantities: deltas,
        deliveryChargeQuantity: delivery,
        prepaidFittingQuantity: fitting,
        delAmountPaid: null,
        quantityAmountPaid: null,
        invoiceId: null,
        clientPurchaseOrderNo: null,
        isAdjustment: true,
        adjustmentDesc: reason.trim(),
        note: null,
      });
      invalidate(datasheetId);
      toast.success("Adjustment recorded");
      onOpenChange(false);
    } catch (e) {
      toast.error(e instanceof ApiError && e.status === 400 ? "Invalid — a reason is required." : "Could not record the adjustment");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Adjust balance</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Enter a delta per pool (may be negative). An adjustment must record a reason.</p>
        <div className="space-y-3">
          {named.map((s) => (
            <div key={s.index} className="flex items-center gap-3">
              <Label htmlFor={`a-size-${s.index}`} className="w-32">{s.desc}</Label>
              <Input
                id={`a-size-${s.index}`}
                aria-label={s.desc!}
                type="number"
                value={deltas[s.index - 1] || ""}
                onChange={(e) => setDelta(s.index, Number(e.target.value) || 0)}
              />
              <span className="w-28 text-xs text-muted-foreground">now {s.quantityAvail}</span>
            </div>
          ))}

          <div className="flex items-center gap-3">
            <Label htmlFor="a-delivery" className="w-32">Delivery charges</Label>
            <Input id="a-delivery" type="number" value={delivery || ""}
              onChange={(e) => setDelivery(Number(e.target.value) || 0)} />
          </div>

          <div className="flex items-center gap-3">
            <Label htmlFor="a-fitting" className="w-32">Fittings</Label>
            <Input id="a-fitting" type="number" value={fitting || ""}
              onChange={(e) => setFitting(Number(e.target.value) || 0)} />
          </div>

          <div className="space-y-1">
            <Label htmlFor="a-reason">Reason</Label>
            <Input id="a-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. stock count correction" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => void submit()} disabled={!canSave}>
            Save adjustment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
